import React, { useState, useEffect, useRef, useContext } from 'react'
import { useParams } from 'react-router-dom'

import { Button } from 'primereact/button'
import { Tag } from 'primereact/tag'
import { Toast } from 'primereact/toast'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Accordion, AccordionTab } from 'primereact/accordion'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, ImageRun,
  Header, AlignmentType, Table, TableRow, TableCell, WidthType, BorderStyle
} from 'docx'
import { saveAs } from 'file-saver'
import jsPDF from 'jspdf'

import NavBar from '../../componentes/Navbar'
import PublicacoesService from '../../app/service/PublicacoesService'
import ResumoIntimacaoIaService from '../../app/service/ResumoIntimacaoIaService'
import ProcessoService from '../../app/service/ProcessoService'
import HistoricoProcessoService from '../../app/service/HistoricoProcessoService'
import TipoAndamentoProcessualService from '../../app/service/TipoAndamentoProcessualService'
import AtividadeService from '../../app/service/AtividadeService'
import AtividadeComponent from '../atividades/AtividadeComponent'
import AtividadeComponentInclusao from '../atividades/AtividadeComponentInclusao'
import { AuthContext } from '../../main/ProvedorAutenticacao'
import logoILaw from '../seguranca/img/logo_login.png'

import '../../styles/publicacaoModal.css'
import '../home/painel.css'

const publicacoesService = new PublicacoesService()
const resumoIntimacaoIaService = new ResumoIntimacaoIaService()
const processoService = new ProcessoService()
const historicoProcessoService = new HistoricoProcessoService()
const tipoAndamentoService = new TipoAndamentoProcessualService()
const atividadeService = new AtividadeService()

export default function Publicacao() {
  const { codigo } = useParams()
  const toast = useRef(null)
  const { usuarioAutenticado } = useContext(AuthContext)

  const [openSpinner, setOpenSpinner] = useState(false)
  const [publicacao, setPublicacao] = useState(null)
  const [erroPublicacao, setErroPublicacao] = useState(false)
  const [resumo, setResumo] = useState(null)
  const [processoInfo, setProcessoInfo] = useState(null)
  const [modalAtividadeInclusao, setModalAtividadeInclusao] = useState(false)
  const [processoParaAtividade, setProcessoParaAtividade] = useState(null)
  const [atividadesProcesso, setAtividadesProcesso] = useState([])
  const [atividadeSelecionada, setAtividadeSelecionada] = useState({})
  const [modalAtividadePublicacao, setModalAtividadePublicacao] = useState(false)
  const [historicoAtividade] = useState([])

  useEffect(() => {
    const carregarPublicacao = async () => {
      setOpenSpinner(true)
      try {
        const response = await publicacoesService.consultarPublicacao(codigo)
        setPublicacao(response.data)

        const codigoProcesso = response.data.processo && response.data.processo.codigo
        if (codigoProcesso) {
          try {
            const responseProcesso = await processoService.consultarProcessoSimplificado(codigoProcesso)
            setProcessoInfo(responseProcesso.data)
          } catch (error) {
            console.log('Erro ao consultar Processo', error)
          }

          try {
            const responseAtividades = await atividadeService.consultarAtividadePorProcesso(codigoProcesso)
            setAtividadesProcesso(responseAtividades.data || [])
          } catch (error) {
            console.log('Erro ao consultar Atividades do Processo', error)
            toast.current && toast.current.show({severity:'error', summary: 'Atividades', detail: 'Erro ao carregar atividades do processo', life: 4000})
          }
        }

        try {
          const historico = await resumoIntimacaoIaService.consultarHistorico(codigo)
          const resumoExistente = historico.data.find(item => item.status === 'CONCLUIDO')
          if (resumoExistente) {
            setResumo(resumoExistente)
          }
        } catch (error) {
          console.log('Erro ao consultar histórico de Resumo', error)
        }
      } catch (error) {
        console.log('Erro ao consultar Publicação', error)
        setErroPublicacao(true)
        toast.current && toast.current.show({severity:'error', summary: 'Consulta de Publicação', detail: 'Publicação não encontrada', life: 4000})
      } finally {
        setOpenSpinner(false)
      }
    }

    carregarPublicacao()
  }, [codigo])

  useEffect(() => {
    document.title = `Publicação ${codigo}`
    return () => { document.title = 'iLaw - Gestão Jurídica' }
  }, [codigo])

  const resumirPublicacao = async () => {
    setOpenSpinner(true)

    try {
      const historico = await resumoIntimacaoIaService.consultarHistorico(codigo)
      const resumoExistente = historico.data.find(item => item.status === 'CONCLUIDO')
      if (resumoExistente) {
        setResumo(resumoExistente)
        setOpenSpinner(false)
        return
      }
    } catch (error) {
      console.log('Erro ao consultar histórico de Resumo de Intimação', error)
    }

    await resumoIntimacaoIaService.gerarResumo(codigo)
      .then(response => {
        setResumo(response.data)
        setOpenSpinner(false)
      }).catch(error => {
        console.log('Erro Resumo de Intimação', error)
        setOpenSpinner(false)
        toast.current && toast.current.show({severity:'error', summary: 'Resumo de Intimação', detail: 'Erro ao gerar o resumo da intimação', life: 4000})
      })
  }

  const safeText = (valor) => {
    if (!valor) return ''
    if (typeof valor === 'string') return valor
    return JSON.stringify(valor)
  }

  const carregarImagemBase64 = async (src) => {
    const response = await fetch(src)
    const blob = await response.blob()
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.readAsDataURL(blob)
    })
  }

  const carregarImagemBuffer = async (imagem) => {
    const response = await fetch(imagem)
    return await response.arrayBuffer()
  }

  const desenharCabecalhoPDF = async (doc) => {
    const logoBase64 = await carregarImagemBase64(logoILaw)
    doc.addImage(logoBase64, 'PNG', 10, 10, 40, 20)

    doc.setFontSize(10)
    doc.text('Fones: (83) 9.98130-0623', 60, 15)
    doc.text('contato@ilawgestao.com.br', 60, 20)
    doc.text('www.ilawgestao.com.br', 60, 25)

    doc.setFontSize(11)
    doc.text('Cliente : Cliente', 10, 40)

    doc.line(10, 43, 200, 43)

    return 55
  }

  const criarHeaderDocx = async () => {
    const imageBuffer = await carregarImagemBuffer(logoILaw)
    return new Header({
      children: [
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.NONE },
            bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE },
            right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE },
            insideVertical: { style: BorderStyle.NONE }
          },
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  width: { size: 30, type: WidthType.PERCENTAGE },
                  borders: { top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE }, left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE } },
                  children: [
                    new Paragraph({
                      children: [
                        new ImageRun({
                          data: imageBuffer,
                          transformation: { width: 120, height: 60 }
                        })
                      ]
                    })
                  ]
                }),
                new TableCell({
                  width: { size: 70, type: WidthType.PERCENTAGE },
                  borders: { top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE }, left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE } },
                  children: [
                    new Paragraph({ alignment: AlignmentType.LEFT, text: 'Fones: (83) 9.98130-0623' }),
                    new Paragraph({ alignment: AlignmentType.LEFT, text: 'contato@ilawgestao.com.br' }),
                    new Paragraph({ alignment: AlignmentType.LEFT, text: 'www.ilawgestao.com.br' })
                  ]
                })
              ]
            })
          ]
        }),
        new Paragraph(''),
        new Paragraph({
          children: [
            new TextRun({ text: 'Cliente : ', bold: true }),
            new TextRun('Cliente')
          ]
        }),
        new Paragraph({
          border: {
            bottom: { color: 'auto', space: 1, style: BorderStyle.SINGLE, size: 6 }
          }
        })
      ]
    })
  }

  const gerarPDFPublicacao = async () => {
    if (!publicacao) return
    const doc = new jsPDF()
    let y = await desenharCabecalhoPDF(doc)

    doc.setFontSize(16)
    doc.text('Publicação Jurídica', 10, y)
    y += 10

    doc.setFontSize(11)
    doc.text(`Data da Publicação: ${safeText(publicacao.dataPublicacao)}`, 10, y)
    y += 7
    doc.text(`Tribunal: ${safeText(publicacao.tribunal)}`, 10, y)
    y += 7
    doc.text(`Processo: ${safeText(processoInfo?.nrCnj || processoInfo?.nrProcesso)}`, 10, y)
    y += 7
    if (processoInfo?.grupoTrabalho?.nome) {
      doc.text(`Grupo de Trabalho: ${safeText(processoInfo.grupoTrabalho.nome)}`, 10, y)
      y += 7
    }
    if (processoInfo?.areaAtuacao?.nome) {
      doc.text(`Área de Atuação: ${safeText(processoInfo.areaAtuacao.nome)}`, 10, y)
      y += 7
    }
    y += 5

    doc.setFontSize(13)
    doc.text('Publicação', 10, y)
    y += 8

    doc.setFontSize(11)
    const textoPublicacao = doc.splitTextToSize(safeText(publicacao.pulicacao), 180)
    doc.text(textoPublicacao, 10, y)
    y += textoPublicacao.length * 6 + 5

    if (processoInfo?.partes?.length > 0) {
      doc.setFontSize(13)
      doc.text('Partes', 10, y)
      y += 8

      doc.setFontSize(11)
      processoInfo.partes.forEach(parte => {
        doc.text(`• ${safeText(parte.pessoa?.nome)} (${safeText(parte.nomeTipo)})`, 10, y)
        y += 6
      })
    }

    doc.save(`publicacao_${codigo}.pdf`)
  }

  const gerarWordPublicacao = async () => {
    if (!publicacao) return
    const header = await criarHeaderDocx()

    const doc = new Document({
      sections: [
        {
          headers: { default: header },
          children: [
            new Paragraph({ text: 'Publicação Jurídica', heading: HeadingLevel.HEADING_1 }),
            new Paragraph(''),
            new Paragraph({
              children: [
                new TextRun({ text: 'Data da Publicação: ', bold: true }),
                new TextRun(safeText(publicacao.dataPublicacao))
              ]
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Tribunal: ', bold: true }),
                new TextRun(safeText(publicacao.tribunal))
              ]
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Processo: ', bold: true }),
                new TextRun(safeText(processoInfo?.nrCnj || processoInfo?.nrProcesso))
              ]
            }),
            ...(processoInfo?.grupoTrabalho?.nome ? [new Paragraph({
              children: [
                new TextRun({ text: 'Grupo de Trabalho: ', bold: true }),
                new TextRun(safeText(processoInfo.grupoTrabalho.nome))
              ]
            })] : []),
            ...(processoInfo?.areaAtuacao?.nome ? [new Paragraph({
              children: [
                new TextRun({ text: 'Área de Atuação: ', bold: true }),
                new TextRun(safeText(processoInfo.areaAtuacao.nome))
              ]
            })] : []),
            new Paragraph(''),
            new Paragraph({ text: 'Publicação', heading: HeadingLevel.HEADING_2 }),
            new Paragraph(safeText(publicacao.pulicacao)),
            new Paragraph(''),
            ...(processoInfo?.partes?.length > 0 ? [
              new Paragraph({ text: 'Partes', heading: HeadingLevel.HEADING_2 }),
              ...processoInfo.partes.map(parte =>
                new Paragraph(`• ${safeText(parte.pessoa?.nome)} (${safeText(parte.nomeTipo)})`)
              )
            ] : [])
          ]
        }
      ]
    })

    const blob = await Packer.toBlob(doc)
    saveAs(blob, `publicacao_${codigo}.docx`)
  }

  const gerarPDFResumo = async () => {
    if (!resumo) return
    const doc = new jsPDF()
    let y = await desenharCabecalhoPDF(doc)

    doc.setFontSize(16)
    doc.text('Resumo de Intimação', 10, y)
    y += 10

    doc.setFontSize(11)
    doc.text(`Processo: ${safeText(processoInfo?.nrCnj || processoInfo?.nrProcesso)}`, 10, y)
    y += 7
    doc.text(`Prazo: ${resumo.prazoExtraido !== null && resumo.prazoExtraido !== undefined ? `${resumo.prazoExtraido} dias` : '-'}`, 10, y)
    y += 7
    doc.text(`Urgência: ${safeText(resumo.urgencia) || '-'}`, 10, y)
    y += 12

    doc.setFontSize(13)
    doc.text('Texto do Resumo', 10, y)
    y += 8

    doc.setFontSize(11)
    const textoResumo = doc.splitTextToSize(safeText(resumo.resumo), 180)
    doc.text(textoResumo, 10, y)

    doc.save(`resumo_publicacao_${codigo}.pdf`)
  }

  const gerarWordResumo = async () => {
    if (!resumo) return
    const header = await criarHeaderDocx()

    const doc = new Document({
      sections: [
        {
          headers: { default: header },
          children: [
            new Paragraph({ text: 'Resumo de Intimação', heading: HeadingLevel.HEADING_1 }),
            new Paragraph(''),
            new Paragraph({
              children: [
                new TextRun({ text: 'Processo: ', bold: true }),
                new TextRun(safeText(processoInfo?.nrCnj || processoInfo?.nrProcesso))
              ]
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Prazo: ', bold: true }),
                new TextRun(resumo.prazoExtraido !== null && resumo.prazoExtraido !== undefined ? `${resumo.prazoExtraido} dias` : '-')
              ]
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Urgência: ', bold: true }),
                new TextRun(safeText(resumo.urgencia) || '-')
              ]
            }),
            new Paragraph(''),
            new Paragraph({ text: 'Texto do Resumo', heading: HeadingLevel.HEADING_2 }),
            new Paragraph(safeText(resumo.resumo))
          ]
        }
      ]
    })

    const blob = await Packer.toBlob(doc)
    saveAs(blob, `resumo_publicacao_${codigo}.docx`)
  }

  const parsePrazoExtraido = (prazo) => {
    if (prazo === null || prazo === undefined) return null
    if (typeof prazo === 'number') {
      const data = new Date()
      data.setDate(data.getDate() + prazo)
      return data
    }
    return null
  }

  const abrirModalCriarAtividade = async (codigoProcesso) => {
    setOpenSpinner(true)
    try {
      const response = await processoService.consultarProcessosPublicacao(codigoProcesso)
      setProcessoParaAtividade(response.data)
      setModalAtividadeInclusao(true)
    } catch (error) {
      console.log('Erro ao consultar processo para atividade', error)
      toast.current && toast.current.show({severity:'error', summary: 'Atividade', detail: 'Erro ao carregar dados do processo', life: 4000})
    } finally {
      setOpenSpinner(false)
    }
  }

  const criarAtividade = async () => {
    const codigoProcesso = publicacao?.processo?.codigo
    if (!codigoProcesso) {
      toast.current && toast.current.show({severity:'warn', summary: 'Atividade', detail: 'Publicação não possui processo vinculado', life: 4000})
      return
    }

    if (atividadesProcesso.length > 0) {
      let dialogRef
      dialogRef = confirmDialog({
        message: 'Já existe uma atividade associada a esta publicação. Deseja criar uma outra atividade com o resumo ou adicionar este resumo a atividade já existente ?',
        header: 'Atividade já existente',
        icon: 'pi pi-question-circle',
        footer: () => (
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: '8px' }}>
            <Button label="Cancelar"
              className="p-button-text"
              onClick={() => dialogRef.hide()} />
            <Button label="Adicionar à Atividade Existente"
              icon="pi pi-file-edit"
              className="p-button-outlined"
              onClick={() => { dialogRef.hide(); adicionarResumoAtividadeExistente() }} />
            <Button label="Criar Nova Atividade"
              icon="pi pi-plus"
              onClick={() => { dialogRef.hide(); abrirModalCriarAtividade(codigoProcesso) }} />
          </div>
        )
      })
      return
    }

    await abrirModalCriarAtividade(codigoProcesso)
  }

  const adicionarResumoAtividadeExistente = async () => {
    const atividadeAlvo = atividadesProcesso[0]
    if (!atividadeAlvo || !resumo?.resumo) return

    setOpenSpinner(true)
    try {
      const responseAtividade = await atividadeService.consultarAtividadeCustom(
        atividadeAlvo.codigo,
        usuarioAutenticado.empresa.codigo,
        usuarioAutenticado.codigo
      )
      const dto = responseAtividade.data

      const hora = (dto.tipo === 'A' && dto.hora) ? dto.hora : '00:00'
      const dataLimiteFormatada = `${dto.dataLimite} ${hora}:00`
      const dataFatalFormatada = dto.prazoFatal ? `${dto.prazoFatal} ${hora}:00` : null

      const atividadeAlterar = {
        atividade: {
          codigo: dto.codigo,
          titulo: dto.titulo,
          tipo: dto.tipo,
          descricao: resumo.resumo,
          usuario: usuarioAutenticado,
          processo: { codigo: dto.codigoProcesso },
          dtLimite: dataLimiteFormatada,
          dtFatal: dataFatalFormatada,
          status: dto.statusEntty,
          subGrupo: { codigo: dto.codigoSubGrupo, grupoPai: dto.codigoGrupo },
          importante: dto.importante,
          urgente: dto.urgente,
          privado: dto.privado
        },
        responsaveis: dto.responsaveisAtividade,
        interessados: dto.interessadosAtividade
      }

      await atividadeService.alterarAtividade(dataLimiteFormatada, dto.statusCodigo, usuarioAutenticado.codigo, atividadeAlterar)

      const responseAtualizada = await atividadeService.consultarAtividadeCustom(
        dto.codigo,
        usuarioAutenticado.empresa.codigo,
        usuarioAutenticado.codigo
      )
      setAtividadeSelecionada(responseAtualizada.data)
      setModalAtividadePublicacao(true)
      atualizarAtividadeAlterada(responseAtualizada.data)

      toast.current && toast.current.show({severity:'success', summary: 'Atividades', detail: 'Atividade atualizada com sucesso', life: 4000})
    } catch (error) {
      console.log('Erro ao adicionar resumo à atividade existente', error)
      toast.current && toast.current.show({severity:'error', summary: 'Atividades', detail: 'Erro ao atualizar a atividade', life: 4000})
    } finally {
      setOpenSpinner(false)
    }
  }

  const registrarComentarioResumo = async (atividadeCriada) => {
    try {
      await atividadeService.incluirHistorico({
        atividade: { codigo: atividadeCriada.codigo },
        dsHistorico: `Resumo de intimação feita por ${usuarioAutenticado.nome}`,
        usuario: usuarioAutenticado,
        tpHistorico: 'A'
      })
    } catch (error) {
      console.log('Erro ao registrar comentário de resumo no histórico', error)
    }
  }

  const gravarEmAndamentos = async () => {
    if (!resumo?.resumo) {
      toast.current && toast.current.show({severity:'warn', summary: 'Andamentos', detail: 'Não há resumo gerado para gravar', life: 4000})
      return
    }
    const codigoProcesso = publicacao?.processo?.codigo
    if (!codigoProcesso) {
      toast.current && toast.current.show({severity:'warn', summary: 'Andamentos', detail: 'Publicação não possui processo vinculado', life: 4000})
      return
    }

    setOpenSpinner(true)
    try {
      const listaTipos = await tipoAndamentoService.listar(usuarioAutenticado.empresa.codigo)
      const NOME_TIPO = 'Resumo de Intimação'
      let tipoAndamento = listaTipos.data.find(t => t.nome.toLowerCase() === NOME_TIPO.toLowerCase())

      if (!tipoAndamento) {
        const criado = await tipoAndamentoService.cadastrar({ nome: NOME_TIPO, empresa: usuarioAutenticado.empresa })
        tipoAndamento = criado.data
      }

      const hoje = new Date()
      const dataHoje = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`

      await historicoProcessoService.incluirHistorico({
        processo: { codigo: codigoProcesso },
        historico: resumo.resumo,
        tipoAndamento,
        dataOcorrencia: dataHoje,
        dataHistorico: dataHoje,
        usuario: usuarioAutenticado,
        publicacao: { codigo: publicacao.codigo }
      })

      toast.current && toast.current.show({severity:'success', summary: 'Andamentos', detail: 'Resumo gravado nos andamentos processuais', life: 4000})
    } catch (error) {
      console.log('Erro ao gravar em andamentos', error)
      toast.current && toast.current.show({severity:'error', summary: 'Andamentos', detail: 'Erro ao gravar nos andamentos processuais', life: 4000})
    } finally {
      setOpenSpinner(false)
    }
  }

  const getTribunalColor = (tribunal) => {
    if (!tribunal) return 'info'
    if (tribunal.includes('TRT')) return 'warning'
    if (tribunal.includes('TJ')) return 'success'
    if (tribunal.includes('STJ')) return 'danger'
    return 'info'
  }

  const getStatusResumoColor = (status) => {
    switch (status) {
      case 'CONCLUIDO': return 'success'
      case 'ERRO': return 'danger'
      case 'PROCESSANDO': return 'info'
      case 'PENDENTE': return 'warning'
      default: return 'info'
    }
  }

  const getUrgenciaColor = (urgencia) => {
    if (!urgencia) return 'info'
    const valor = urgencia.toLowerCase()
    if (valor.includes('alta') || valor.includes('urgente')) return 'danger'
    if (valor.includes('media') || valor.includes('média')) return 'warning'
    if (valor.includes('baixa')) return 'success'
    return 'info'
  }

  const getUrgenciaClass = (urgencia) => {
    if (!urgencia) return ''
    const u = urgencia.toLowerCase()
    if (u.includes('alta') || u.includes('urgente')) return 'alta'
    if (u.includes('media') || u.includes('média')) return 'media'
    if (u.includes('baixa')) return 'baixa'
    return ''
  }

  const statusTemplate = (data) => (
    <Tag className="p-mr-2" style={{backgroundColor: `${data.status?.cor}`}} value={data.status?.status}></Tag>
  )

  const grauTemplate = (data) => {
    if (data.importante === 'S' && data.urgente === 'S') {
      return (
        <>
          <Tag style={{backgroundColor: 'red', width: '50px'}} value="Urgente"></Tag>
          &nbsp;
          <Tag style={{backgroundColor: 'green', width: '65px'}} value="Importante"></Tag>
        </>
      )
    } else if (data.importante === 'S' && data.urgente === 'N') {
      return <Tag style={{backgroundColor: 'green', width: '65px'}} value="Importante"></Tag>
    } else if (data.importante === 'N' && data.urgente === 'S') {
      return <Tag style={{backgroundColor: 'red', width: '65px'}} value="Urgente"></Tag>
    }
    return null
  }

  const prazoFatalFormat = (data) => {
    if (data.dtFatal != null) {
      return <div><Tag severity="danger" value={data.dtFatal}></Tag></div>
    }
    return null
  }

  const subGrupoTemplate = (data) => data.subGrupo?.nome

  const linkAbrirAtividade = (rowData) => (
    <a onClick={() => consultarAtividade(rowData)} style={{cursor: 'pointer', color: 'blue'}}>{rowData.titulo}</a>
  )

  const consultarAtividade = async (atividade) => {
    setOpenSpinner(true)
    try {
      const response = await atividadeService.consultarAtividadeCustom(
        atividade.codigo,
        usuarioAutenticado.empresa.codigo,
        usuarioAutenticado.codigo
      )
      setAtividadeSelecionada(response.data)
      setModalAtividadePublicacao(true)
    } catch (error) {
      console.log('Erro ao consultar Atividade', error)
      toast.current && toast.current.show({severity:'error', summary: 'Atividades', detail: `${error}`, life: 4000})
    } finally {
      setOpenSpinner(false)
    }
  }

  const atualizarAtividadeAlterada = (atividadeAlterada) => {
    setAtividadesProcesso(prev =>
      prev.map(atv => (atv.codigo === atividadeAlterada.codigo ? atividadeAlterada : atv))
    )
  }

  return (
    <>
      <NavBar modoFoco={true}/>
      <Toast ref={toast} />
      <ConfirmDialog />
      <AtividadeComponentInclusao
        visible={modalAtividadeInclusao}
        processo={processoParaAtividade}
        publicacao={publicacao}
        descricaoInicial={resumo?.resumo || ''}
        dtLimiteInicial={parsePrazoExtraido(resumo?.prazoExtraido)}
        urgenciaResumo={resumo?.urgencia || ''}
        onHide={() => setModalAtividadeInclusao(false)}
        onAtividadeCriada={registrarComentarioResumo}
      />
      <AtividadeComponent
        visible={modalAtividadePublicacao}
        atividade={atividadeSelecionada}
        historico={historicoAtividade}
        onHide={() => setModalAtividadePublicacao(false)}
        onAtividadeAlterada={atualizarAtividadeAlterada}
      />
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openSpinner}>
        <CircularProgress color="primary" style={{width: '130px', height: '130px'}}/>
      </Backdrop>

      <div className="pub-page-bg">
        <div className="pub-page">
          {erroPublicacao ? (
            <div className="pub-panel pub-panel--erro">
              <i className="pi pi-times-circle" style={{fontSize: '3rem', color: '#dc2626'}}></i>
              <p>Publicação não encontrada.</p>
            </div>
          ) : (
            <>
              {/* Hero strip */}
              <div className="pub-hero">
                <div className="pub-hero__info">
                  <i className="fa fa-gavel pub-hero__icon"></i>
                  <h1 className="pub-hero__title">Publicação {codigo}</h1>
                  {publicacao && publicacao.dataPublicacao && (
                    <span className="pub-hero__date">{publicacao.dataPublicacao}</span>
                  )}
                </div>
                {publicacao && publicacao.tribunal && (
                  <Tag value={publicacao.tribunal} severity={getTribunalColor(publicacao.tribunal)} />
                )}
              </div>

              {/* Main content panel */}
              <div className="pub-panel">
                <div className="pub-panel__body">

                  {/* Left info column */}
                  <div className="pub-info-col">
                    {publicacao && publicacao.link && (
                      <div className="pub-info-item">
                        <span className="pub-info-label">Link</span>
                        <a href={publicacao.link} target="_blank" rel="noopener noreferrer" className="pub-info-link">
                          {publicacao.link}
                        </a>
                      </div>
                    )}
                    {processoInfo && (
                      <>
                        <div className="pub-info-item">
                          <span className="pub-info-label">Processo</span>
                          <span className="pub-info-value pub-info-value--strong">
                            {(processoInfo.nrCnj || processoInfo.nrProcesso) || '—'}
                            {processoInfo.pasta ? ` · Pasta ${processoInfo.pasta}` : ''}
                          </span>
                        </div>
                        {processoInfo.grupoTrabalho && (
                          <div className="pub-info-item">
                            <span className="pub-info-label">Grupo de Trabalho</span>
                            <span className="pub-info-value">{processoInfo.grupoTrabalho.nome}</span>
                          </div>
                        )}
                        {processoInfo.areaAtuacao && (
                          <div className="pub-info-item">
                            <span className="pub-info-label">Área de Atuação</span>
                            <span className="pub-info-value">{processoInfo.areaAtuacao.nome}</span>
                          </div>
                        )}
                        {processoInfo.partes && processoInfo.partes.length > 0 && (
                          <div className="pub-info-item">
                            <span className="pub-info-label">Partes</span>
                            <div className="pub-partes">
                              {processoInfo.partes.map((parte, index) => (
                                <div key={index} className="pub-parte-chip">
                                  <span className="pub-parte-nome">{parte.pessoa.nome}</span>
                                  <span className="pub-parte-tipo">{parte.nomeTipo}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Right publication text */}
                  <div className="pub-texto-col">
                    <p className="pub-texto-content">
                      {publicacao && publicacao.pulicacao}
                    </p>
                  </div>

                </div>

                <div className="pub-actions-bar">
                  <Button label="Resumir Publicação"
                    icon="pi pi-align-justify"
                    disabled={!!resumo}
                    className="pub-action-btn pub-action-btn--primary"
                    onClick={resumirPublicacao} />
                  <Button label="Imprimir PDF"
                    icon="pi pi-file-pdf"
                    className="pub-action-btn pub-action-btn--outline-danger"
                    onClick={gerarPDFPublicacao} />
                  <Button label="Imprimir Word"
                    icon="pi pi-file"
                    className="pub-action-btn pub-action-btn--outline-info"
                    onClick={gerarWordPublicacao} />
                </div>
              </div>

              {/* Atividades associadas */}
              <div className="pub-atividades-panel">
                <Accordion multiple>
                  <AccordionTab header="Atividades associadas a esta publicação">
                    <DataTable value={atividadesProcesso}
                               responsiveLayout="scroll" size="small"
                               emptyMessage="Nenhuma Atividade">
                      <Column body={statusTemplate} exportable={false} style={{ minWidth: '8rem' }} header="Status"/>
                      <Column body={linkAbrirAtividade} exportable={false} style={{ minWidth: '8rem' }} header="Título"></Column>
                      <Column body={grauTemplate} exportable={false} style={{ minWidth: '8rem' }} header="Grau"/>
                      <Column body={subGrupoTemplate} header="Sub-Grupo"></Column>
                      <Column field="responsaveis" header="Responsáveis"></Column>
                      <Column field="dtLimite" header="Data Limite"></Column>
                      <Column body={prazoFatalFormat} exportable={false} style={{ minWidth: '8rem' }} header="Prazo Fatal"></Column>
                    </DataTable>
                  </AccordionTab>
                </Accordion>
              </div>

              {/* Resumo IA panel */}
              {resumo && (
                <div className={`pub-resumo-panel${getUrgenciaClass(resumo.urgencia) ? ` pub-resumo--${getUrgenciaClass(resumo.urgencia)}` : ''}`}>
                  <div className="pub-resumo-header">
                    <i className="fa fa-robot"></i>
                    <span>Resumo de Intimação · IA</span>
                    <Tag value={resumo.status} severity={getStatusResumoColor(resumo.status)} className="pub-resumo-status" />
                  </div>

                  {resumo.urgencia && (
                    <div className={`pub-urgencia-banner pub-urgencia--${getUrgenciaClass(resumo.urgencia)}`}>
                      <i className="pi pi-exclamation-triangle"></i>
                      Urgência: {resumo.urgencia}
                    </div>
                  )}

                  {resumo.prazoExtraido !== null && resumo.prazoExtraido !== undefined && (
                    <div className="pub-prazo-destaque">
                      <i className="pi pi-calendar"></i>
                      <div>
                        <span className="pub-prazo-label">Prazo Extraído</span>
                        <span className="pub-prazo-valor">{resumo.prazoExtraido} dias</span>
                      </div>
                    </div>
                  )}

                  <div className="pub-resumo-texto-wrap">
                    <p className="pub-resumo-texto">
                      {resumo.resumo}
                    </p>
                  </div>

                  {resumo.lastError && (
                    <div className="pub-resumo-erro">
                      <i className="pi pi-times-circle"></i>
                      {resumo.lastError}
                    </div>
                  )}

                  <div className="pub-actions-bar pub-actions-bar--resumo">
                    <Button label="Criar Atividade"
                      icon="pi pi-plus"
                      className="pub-action-btn pub-action-btn--primary"
                      onClick={criarAtividade} />
                    <Button label="Gravar em Andamentos"
                      icon="pi pi-save"
                      className="pub-action-btn pub-action-btn--outline-success"
                      onClick={() => confirmDialog({
                        message: 'Deseja realmente adicionar este resumo aos andamentos processuais?',
                        header: 'Gravar em Andamentos',
                        icon: 'pi pi-question-circle',
                        acceptLabel: 'Sim',
                        rejectLabel: 'Não',
                        accept: gravarEmAndamentos
                      })} />
                    <Button label="Imprimir PDF"
                      icon="pi pi-file-pdf"
                      className="pub-action-btn pub-action-btn--outline-danger"
                      onClick={gerarPDFResumo} />
                    <Button label="Imprimir Word"
                      icon="pi pi-file"
                      className="pub-action-btn pub-action-btn--outline-info"
                      onClick={gerarWordResumo} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
